import { styled } from "goober";
import { queries } from "../../../shared/queries";
import { useFocusShortcut } from "../../hooks/useFocusShortcut";
import { useQuery } from "@rocicorp/zero/react";
import { navigate, routes, useCurrentUrl } from "../../lib/urls";
import { Backdrop } from "../Backdrop";
import { ClearButton } from "../ClearButton";
import { Icon } from "../Icon";
import { Input } from "../Input";
import { MainScrollContainer } from "../MainScrollContainer";
import { HeaderCountBadge } from "../Settings/shared";
import { TabHeaderBar } from "../TabHeader";
import { Text } from "../Text";

const Container = styled("div")`
  flex: 1;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 16px;
  box-shadow: var(--elevation-low);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Heading = styled("div")`
  display: flex;
  align-items: center;
  height: 56px;
  padding: 0 24px;
`;

const SearchRow = styled("div")`
  margin-left: auto;
  margin-right: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SearchContainer = styled("div")`
  position: relative;
`;

const SearchIcon = styled("div")`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-primary);
  pointer-events: none;
`;

type Props = {
  children: React.ReactNode;
  searchAction?: React.ReactNode;
};

export function SkillsLayout({ children, searchAction }: Props) {
  const listUrl = useCurrentUrl(routes.skills.list);
  const query = listUrl.q ?? "";
  const searchRef = useFocusShortcut<HTMLInputElement>();

  const [skills, skillsDetails] = useQuery(queries.skillsIndex());
  const count = skills.length;

  const setQuery = (q: string) =>
    navigate(routes.skills.list, { q }, { replace: true });

  return (
    <Backdrop permanentNavigation>
      <Container>
        <TabHeaderBar>
          <Heading>
            <Text variant="text-regular-md">Skills</Text>
            {skillsDetails.type === "complete" && (
              <HeaderCountBadge count={count} />
            )}
          </Heading>
          <SearchRow>
            <SearchContainer>
              <SearchIcon>
                <Icon name="search" size={12} />
              </SearchIcon>
              <Input
                ref={searchRef}
                data-search
                placeholder="Search…"
                value={query}
                autoFocus
                onChange={(e) => setQuery(e.target.value)}
              />
              {query && (
                <ClearButton
                  onPress={() => {
                    setQuery("");
                    searchRef.current?.focus();
                  }}
                >
                  <Icon name="x" size={12} />
                </ClearButton>
              )}
            </SearchContainer>
            {searchAction}
          </SearchRow>
        </TabHeaderBar>

        <MainScrollContainer>{children}</MainScrollContainer>
      </Container>
    </Backdrop>
  );
}
